import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { compileRequest, countLeads, FILTER_FIELDS, filterSentence, IFilter, IStarter, STARTERS, TFilterField, VOICES } from './data'
import { Frame, Screen } from './Frame'
import { useFooter } from './footer-context'
import { useCyclingPlaceholder } from './hooks'
import { Circle } from './motion'
import { seatCount, useFirstRun } from './store'
import { Avatar, Button, Chip, Icon, Input, Radio, Select, Tile, VoiceOrb } from './ui'

type TStageId = 'who' | 'routing' | 'voice' | 'numbers' | 'hours' | 'book' | 'launching'

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const LAUNCH = ['Creating the campaign', 'Buying your local numbers', 'Setting up the voice', 'Going live']

const hoursText = (days: boolean[], from: string, to: string) => {
   const on = DAYS.filter((_, i) => days[i])
   if (!on.length) return 'No days picked'
   const span = on.length === 7 ? 'Every day' : on.length === 1 ? on[0] : `${on[0]} to ${on[on.length - 1]}`
   const clock = (t: string) => {
      const [h, m] = t.split(':').map(Number)
      return `${h % 12 || 12}${m ? ':' + String(m).padStart(2, '0') : ''} ${h >= 12 ? 'pm' : 'am'}`
   }
   return `${span}, ${clock(from)} to ${clock(to)}`
}

const ROUTING = [
   { id: 'round-robin', title: 'Take turns', body: 'Each interested lead goes to the next teammate in line.' },
   { id: 'jump-ball', title: 'First to answer', body: 'Everyone’s phone rings. Whoever picks up first takes the lead.' },
   { id: 'me', title: 'Just me', body: 'Every interested lead is transferred to your mobile.' },
] as const

const fieldLabel = (f: TFilterField) => FILTER_FIELDS.find((x) => x.id === f)?.label || f
const chipKind = (f: TFilterField) => (f === 'source' || f === 'pond' || f === 'stage' || f === 'tag' ? f : undefined)

/** The left panel for the campaign: what is decided, with Edit on each row. */
interface IRailRow {
   id: string
   label: string
   icon: string
   value: string
   sub?: string
   pending?: boolean
   onEdit?: () => void
}
const CampaignRail = ({ rows, stage }: { rows: IRailRow[]; stage: TStageId }) => (
   <ol className="flex-1 flex flex-col py-2" data-testid="glimpse">
      {rows.map((r) => {
         const current = r.id === stage
         return (
            <li key={r.id} data-testid={`glimpse-${r.id}`} data-state={r.pending ? 'pending' : 'set'} className="flex-1 min-h-0 max-h-[80px] flex items-center pr-4">
               <div className={cn('group w-full flex items-center gap-3 px-5 py-2.5 rounded-xl mx-2', current && 'bg-[var(--fr-blue-tint)]')}>
                  <span className={cn('flex shrink-0 items-center justify-center w-9 h-9 rounded-full', current ? 'bg-[var(--fr-blue)] text-white' : r.pending ? 'bg-[var(--fr-surface-2)] text-[var(--fr-text-3)]' : 'bg-[var(--fr-good-tint)] text-[var(--fr-good)]')}>
                     <Icon name={current ? r.icon : r.pending ? r.icon : 'check'} size={16} />
                  </span>
                  <span className="flex-1 min-w-0">
                     <span className={cn('block text-[14px] font-semibold truncate', r.pending && !current ? 'text-[var(--fr-text-3)]' : 'text-[var(--fr-text)]')}>{r.label}</span>
                     <span className={cn('block text-[12px] truncate', current ? 'text-[var(--fr-blue)]' : r.pending ? 'text-[var(--fr-text-3)]' : 'text-[var(--fr-text-2)]')}>
                        {current && r.pending ? 'In progress' : r.value}
                        {r.sub && !r.pending ? <span className="text-[var(--fr-text-3)]"> · {r.sub}</span> : null}
                     </span>
                  </span>
                  {r.onEdit && !current && (
                     <button type="button" onClick={r.onEdit} data-testid={`edit-${r.id}`} className="text-[12px] font-semibold text-[var(--fr-blue)] opacity-0 group-hover:opacity-100 focus:opacity-100 hover:underline">
                        Edit
                     </button>
                  )}
               </div>
            </li>
         )
      })}
   </ol>
)

/** One filter as a row in the confirmation card. */
const FilterRow = ({ f, onRemove, testId }: { f: IFilter; onRemove: () => void; testId?: string }) => (
   <div className="flex items-center gap-3 px-5 min-h-[44px]" data-testid={testId}>
      <span className="w-[110px] shrink-0 text-[13px] font-medium text-[var(--fr-text-2)]">{fieldLabel(f.field)}</span>
      <span className={cn('text-[13px] w-[52px] shrink-0', f.op === 'not' ? 'font-semibold text-[var(--fr-warn)]' : 'text-[var(--fr-text-3)]')}>{f.op === 'not' ? 'is not' : 'is'}</span>
      <span className="flex-1 min-w-0">
         <Chip kind={chipKind(f.field)}>{f.value}</Chip>
      </span>
      <button type="button" aria-label={`Remove ${fieldLabel(f.field)} filter`} onClick={onRemove} className="w-8 h-8 rounded-full text-[var(--fr-text-3)] hover:bg-[var(--fr-surface-2)] hover:text-[var(--fr-text)] flex items-center justify-center">
         <Icon name="close" size={14} />
      </button>
   </div>
)

/** Field and operator on one line, the value and the buttons on the next. */
const AddFilter = ({ onAdd, onCancel }: { onAdd: (f: IFilter) => void; onCancel: () => void }) => {
   const [field, setField] = useState<TFilterField>('source')
   const [op, setOp] = useState<'is' | 'not'>('is')
   const [value, setValue] = useState('')
   const def = FILTER_FIELDS.find((x) => x.id === field)!
   return (
      <div className="px-4 py-3 bg-[var(--fr-blue-tint)]/50 space-y-2" data-testid="add-filter-row">
         <div className="grid grid-cols-2 gap-2">
            <Select value={field} onChange={(v) => { setField(v as TFilterField); setOp('is'); setValue('') }} options={FILTER_FIELDS.map((x) => ({ value: x.id, label: x.label }))} placeholder="" testId="filter-field" />
            <Select value={op} onChange={(v) => setOp(v as 'is' | 'not')} options={def.ops.map((o) => ({ value: o, label: o === 'is' ? 'is' : 'is not' }))} placeholder="" testId="filter-op" />
         </div>
         <div className="flex items-center gap-2">
            <Select value={value} onChange={setValue} options={def.values()} placeholder="Choose a value" testId="filter-value" className="flex-1" />
            <Button disabled={!value} onClick={() => value && onAdd({ field, op, value })} testId="filter-add">Add</Button>
            <Button variant="ghost" onClick={onCancel}>Cancel</Button>
         </div>
      </div>
   )
}

const ComposerInner = ({ stage, setStage, returnTo, setReturnTo }: { stage: TStageId; setStage: (s: TStageId) => void; returnTo: TStageId; setReturnTo: (s: TStageId) => void }) => {
   const navigate = useNavigate()
   const s = useFirstRun()
   const { draft, startDraft, updateDraft, areaCodes, set, owner, team, selectedTeam, launch, launched } = s
   const [text, setText] = useState(draft?.text || '')
   const [adding, setAdding] = useState(false)
   const placeholder = useCyclingPlaceholder(!text)
   const [callDays, setCallDays] = useState([true, true, true, true, true, true, false])
   const [callFrom, setCallFrom] = useState('09:00')
   const [callTo, setCallTo] = useState('18:00')
   const [bookDays, setBookDays] = useState([true, true, true, true, true, false, false])
   const [bookFrom, setBookFrom] = useState('09:00')
   const [bookTo, setBookTo] = useState('17:00')
   const [launchStep, setLaunchStep] = useState(0)
   const [areaSearch, setAreaSearch] = useState('')
   const [playing, setPlaying] = useState<string | null>(null)
   const [selectingAgents, setSelectingAgents] = useState(false)

   useEffect(() => {
      if (!draft) startDraft({})
      else if (draft.text && !draft.filters.length) applyWords(draft.text)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   const applyWords = (words: string) => {
      const c = compileRequest(words)
      updateDraft({ text: words, name: c.name, filters: c.filters, leads: countLeads(c.filters) })
      setAdding(false)
   }
   const pickStarter = (st: IStarter) => {
      updateDraft({ text: '', name: st.title, filters: st.filters, leads: st.leads })
      setText('')
   }
   const setFilters = (filters: IFilter[]) => updateDraft({ filters, leads: countLeads(filters) })

   const seats = seatCount(s)
   const voice = VOICES.find((v) => v.id === draft?.voiceId) || VOICES[0]
   const confirming = Boolean(draft && draft.filters.length > 0)
   const ready = Boolean(draft && confirming && draft.leads > 0)
   const members = team.filter((p) => selectedTeam.includes(p.id))
   const people = [{ id: owner.id, name: owner.name, sub: owner.phone || owner.email }, ...members.map((p) => ({ id: p.id, name: p.name, sub: p.phone || p.email })), ...s.invited.map((e) => ({ id: e, name: e, sub: 'Invited' }))]
   const voiceIndex = Math.max(0, VOICES.findIndex((v) => v.id === voice.id))
   const prevVoice = VOICES[(voiceIndex - 1 + VOICES.length) % VOICES.length]
   const nextVoice = VOICES[(voiceIndex + 1) % VOICES.length]
   const pickVoice = (v: (typeof VOICES)[number]) => {
      setPlaying(null)
      updateDraft({ voiceId: v.id, assistantName: draft?.assistantName === voice.name ? v.name : draft?.assistantName })
   }
   const routingLabel = seats === 1 ? 'Just you' : ROUTING.find((r) => r.id === draft?.routing)?.title || 'Take turns'
   const afterWho: TStageId = seats > 1 ? 'routing' : 'voice'

   const openEditor = (next: TStageId) => {
      if (stage === 'who' || stage === 'routing' || stage === 'voice') setReturnTo(stage)
      setStage(next)
   }
   const doLaunch = () => {
      setStage('launching')
      setLaunchStep(0)
      let i = 0
      const tick = () => {
         i += 1
         setLaunchStep(i)
         if (i < LAUNCH.length) setTimeout(tick, 650)
         else
            setTimeout(() => {
               launch()
               navigate('/start/home')
            }, 600)
      }
      setTimeout(tick, 650)
   }
   const home = () => navigate('/start/home')

   const footer = useMemo(() => {
      switch (stage) {
         case 'who':
            return !confirming
               ? { action: text.trim() ? 'Turn this into filters' : 'Describe who to call', onAction: () => applyWords(text), disabled: !text.trim(), back: home, hint: 'Say it the way you would to an assistant, or pick one of the ready campaigns.', testId: 'compile' }
               : { action: `Continue with ${draft?.leads ?? 0} leads`, onAction: () => setStage(afterWho), disabled: !ready, back: home, hint: 'Leads you are working, already done with, or on do-not-call are never called.', testId: 'to-routing' }
         case 'routing':
            return { action: 'Next: choose the voice', onAction: () => setStage('voice'), back: () => setStage('who'), hint: 'Change this later from the campaign page.', testId: 'to-voice' }
         case 'voice':
            return { action: 'Launch campaign', onAction: doLaunch, disabled: !draft?.assistantName.trim(), back: () => setStage(afterWho === 'routing' ? 'routing' : 'who'), hint: `${draft?.leads ?? 0} leads · calls start in your calling hours, ${hoursText(callDays, callFrom, callTo).toLowerCase()}.`, testId: 'launch', icon: 'zap', variant: 'dark' as const }
         case 'launching':
            return { action: 'Launching', onAction: () => undefined, loading: true, back: null, hint: 'Creating the campaign, buying numbers, setting the voice, going live.' }
         default:
            return { action: 'Done', onAction: () => setStage(returnTo), back: null, hint: 'Applies to this campaign only.', testId: 'editor-done' }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [stage, ready, confirming, text, draft?.leads, draft?.assistantName, returnTo, afterWho, callDays, callFrom, callTo])
   useFooter(footer)

   if (!draft) return null

   const dayChips = (days: boolean[], setDays: (d: boolean[]) => void) => (
      <div className="flex flex-wrap justify-center gap-2">
         {DAYS.map((d, i) => (
            <button key={d} type="button" aria-pressed={days[i]} onClick={() => setDays(days.map((v, j) => (j === i ? !v : v)))} className={cn('h-11 w-[64px] rounded-full border text-[14px] font-semibold transition-colors', days[i] ? 'border-[var(--fr-blue)] bg-[var(--fr-blue)] text-white' : 'border-[var(--fr-border)] text-[var(--fr-text-2)] hover:border-[var(--fr-border-strong)]')}>
               {d}
            </button>
         ))}
      </div>
   )
   const timeRow = (from: string, to: string, setFrom: (v: string) => void, setTo: (v: string) => void) => (
      <div className="flex items-end justify-center gap-3 text-[15px]">
         <label className="block"><span className="block text-[13px] font-medium text-[var(--fr-text-2)] mb-1.5">From</span><input type="time" value={from} onChange={(e) => setFrom(e.currentTarget.value)} className="h-12 rounded-2xl border border-[var(--fr-border)] px-4 focus:border-[var(--fr-blue)] focus:outline-none focus:ring-4 focus:ring-[var(--fr-blue-tint)]" /></label>
         <label className="block"><span className="block text-[13px] font-medium text-[var(--fr-text-2)] mb-1.5">To</span><input type="time" value={to} onChange={(e) => setTo(e.currentTarget.value)} className="h-12 rounded-2xl border border-[var(--fr-border)] px-4 focus:border-[var(--fr-blue)] focus:outline-none focus:ring-4 focus:ring-[var(--fr-blue-tint)]" /></label>
      </div>
   )
   const sentence = filterSentence(draft.filters)

   return (
      <>
         {stage === 'who' && !confirming && (
            <Screen stepKey="who-words" kind="who" title={launched.length ? 'Who should this campaign call?' : 'Who should we call first?'} lede="Describe the leads in your own words. We turn it into Follow Up Boss filters you can check before anything goes out." width="lg">
               <div className="mx-auto max-w-[620px]">
                  <Input value={text} onChange={setText} placeholder={placeholder} size="lg" autoFocus testId="describe" onEnter={() => text.trim() && applyWords(text)} align="center" />
               </div>
               <p className="mt-5 text-center text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--fr-text-3)]">Or start from a ready campaign</p>
               <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="starter-cards">
                  {STARTERS.map((st) => {
                     const tone = st.kind === 'new' ? 'blue' : st.kind === 'older' ? 'warm' : 'violet'
                     return (
                        <button key={st.id} type="button" onClick={() => pickStarter(st)} data-testid={`starter-${st.id}`} className={cn('text-left rounded-2xl border p-4 transition-all hover:-translate-y-0.5 hover:shadow-[var(--fr-shadow)] flex flex-col items-start', tone === 'blue' && 'border-[#d6e2ff] bg-[var(--fr-blue-tint)]', tone === 'warm' && 'border-[#f5e1bf] bg-[var(--fr-warn-tint)]', tone === 'violet' && 'border-[#e2d6ff] bg-[var(--fr-pond-tint)]')}>
                           <span className={cn('inline-flex items-center justify-center w-9 h-9 rounded-full text-white', tone === 'blue' && 'bg-[var(--fr-blue)]', tone === 'warm' && 'bg-[var(--fr-warn)]', tone === 'violet' && 'bg-[var(--fr-pond)]')}>
                              <Icon name={st.kind === 'new' ? 'zap' : st.kind === 'older' ? 'clock' : 'users'} size={16} />
                           </span>
                           <span className="block mt-3 text-[15px] font-semibold">{st.title}</span>
                           <span className="block mt-1 text-[12px] leading-4 text-[var(--fr-text-2)]">{st.description}</span>
                           <span className="block mt-2 text-[13px]"><b>{st.leads.toLocaleString()}</b> <span className="text-[var(--fr-text-2)]">leads match</span></span>
                        </button>
                     )
                  })}
               </div>
            </Screen>
         )}
         {stage === 'who' && confirming && (
            <Screen stepKey="who-sentence" kind="who" title="Does this look right?" lede={<span data-testid="sentence">Leads {sentence.join(', ')}. Change the words above the list, or add and remove filters.</span>} width="md">
               <Input value={text} onChange={setText} placeholder={placeholder} testId="describe" onEnter={() => text.trim() && applyWords(text)} leading={<Icon name="edit" size={15} />} trailing={text.trim() && text !== draft.text ? 'Press Enter to apply' : undefined} />
               <div className="mt-3 rounded-[20px] border border-[var(--fr-border)] bg-white divide-y divide-[var(--fr-border)] overflow-hidden" data-testid="filter-card">
                  {draft.filters.map((f, i) => (
                     <FilterRow key={f.field + f.op + f.value} f={f} testId={`filter-${i}`} onRemove={() => setFilters(draft.filters.filter((_, j) => j !== i))} />
                  ))}
                  {adding ? (
                     <AddFilter onAdd={(f) => { setFilters([...draft.filters, f]); setAdding(false) }} onCancel={() => setAdding(false)} />
                  ) : (
                     <button type="button" onClick={() => setAdding(true)} data-testid="add-filter" className="w-full flex items-center gap-3 px-5 h-[48px] text-left text-[14px] font-semibold text-[var(--fr-blue)] hover:bg-[var(--fr-blue-tint)]/50">
                        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[var(--fr-blue-tint)]"><Icon name="plus" size={14} /></span> Add a filter
                     </button>
                  )}
               </div>
               {!adding && <p className="mt-3 text-[15px] text-center" data-testid="lead-count"><b className="text-[var(--fr-blue)]">{draft.leads.toLocaleString()}</b> <span className="text-[var(--fr-text-2)]">leads match today</span></p>}
            </Screen>
         )}

         {stage === 'routing' && (
            <Screen stepKey="routing" kind="routing" title="Who takes the interested leads?" lede={selectingAgents ? 'Untick anyone who should not receive transferred leads for this campaign.' : 'When a lead wants to talk, we transfer the call live. Choose how your team shares them.'} width="sm">
               {selectingAgents ? (
                  <div className="rounded-[20px] border border-[var(--fr-border)] bg-white divide-y divide-[var(--fr-border)] overflow-hidden" data-testid="agent-list">
                     {people.map((p) => {
                        const on = draft.agents.includes(p.id)
                        return (
                           <button key={p.id} type="button" role="checkbox" aria-checked={on} data-testid={`agent-${p.id}`} onClick={() => updateDraft({ agents: on ? draft.agents.filter((x) => x !== p.id) : [...draft.agents, p.id] })} className="w-full flex items-center gap-3 px-4 min-h-[56px] text-left hover:bg-[var(--fr-surface-2)]">
                              <span className={cn('flex shrink-0 items-center justify-center w-5 h-5 rounded-md border-2 transition-colors', on ? 'bg-[var(--fr-blue)] border-[var(--fr-blue)] text-white' : 'border-[var(--fr-border-strong)]')}>{on && <Icon name="check" size={12} />}</span>
                              <Avatar name={p.name} size={30} />
                              <span className="flex-1 min-w-0">
                                 <span className="block text-[14px] font-semibold truncate">{p.name}{p.id === owner.id ? ' (you)' : ''}</span>
                                 <span className="block text-[12px] text-[var(--fr-text-3)] truncate">{p.sub}</span>
                              </span>
                           </button>
                        )
                     })}
                     <div className="flex items-center justify-between px-4 h-[52px] bg-[var(--fr-surface-2)]/60">
                        <span className="text-[13px] text-[var(--fr-text-2)]">{draft.agents.length} of {people.length} selected</span>
                        <Button size="sm" onClick={() => setSelectingAgents(false)} disabled={draft.agents.length === 0} testId="agents-done">Done</Button>
                     </div>
                  </div>
               ) : (
                  <>
                     <div className="space-y-3" role="radiogroup">
                        {ROUTING.map((r) => {
                           const on = draft.routing === r.id
                           return (
                              <Tile key={r.id} selected={on} onClick={() => updateDraft({ routing: r.id })} testId={`routing-${r.id}`}>
                                 <div className="flex items-center gap-3">
                                    <Radio on={on} />
                                    <div className="flex-1">
                                       <p className="text-[15px] font-semibold">{r.title}</p>
                                       <p className="text-[13px] text-[var(--fr-text-2)]">{r.body}</p>
                                    </div>
                                 </div>
                              </Tile>
                           )
                        })}
                     </div>
                     <div className="mt-3 rounded-2xl border border-[var(--fr-border)] bg-white px-4 h-[60px] flex items-center gap-3" data-testid="agents-row">
                        <div className="flex -space-x-2">{people.filter((p) => draft.agents.includes(p.id)).slice(0, 5).map((p) => <span key={p.id} className="rounded-full ring-2 ring-white"><Avatar name={p.name} size={28} /></span>)}</div>
                        <span className="flex-1 min-w-0 text-[13px] text-[var(--fr-text-2)] truncate" data-testid="agents-summary">{draft.agents.length === people.length ? `All ${people.length} agents get leads` : `${draft.agents.length} of ${people.length} agents get leads`}</span>
                        <Button size="sm" variant="secondary" onClick={() => setSelectingAgents(true)} testId="select-agents">Select agents</Button>
                     </div>
                  </>
               )}
            </Screen>
         )}

         {stage === 'voice' && (
            <Screen stepKey="voice" kind="voice" title="Choose the voice your leads will hear" lede="One voice at a time. Use the arrows to compare, press play to listen." width="md">
               <div className="flex items-start justify-center gap-4" data-testid="orbs">
                  <button type="button" aria-label="Previous voice" onClick={() => pickVoice(prevVoice)} data-testid="voice-prev" className="mt-[34px] w-11 h-11 rounded-full border border-[var(--fr-border)] bg-white flex items-center justify-center text-[var(--fr-text-2)] hover:border-[var(--fr-border-strong)] hover:text-[var(--fr-text)]"><Icon name="arrow-left" size={16} /></button>
                  <div className="flex items-start justify-center gap-6 w-[360px]">
                     <button type="button" aria-label={`Switch to ${prevVoice.name}`} onClick={() => pickVoice(prevVoice)} className="mt-8 opacity-30 hover:opacity-60 transition-opacity"><VoiceOrb palette={prevVoice.palette} size={48} /></button>
                     <div className="flex flex-col items-center gap-2 w-[180px]" data-testid="voice-current" data-voice={voice.id}>
                        <VoiceOrb palette={voice.palette} size={112} live={playing === voice.id} />
                        <span className="text-[16px] font-semibold">{voice.name}</span>
                        {voice.id === VOICES[0].id ? <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-[var(--fr-good-tint)] text-[var(--fr-good)] text-[11px] font-semibold uppercase tracking-[0.06em]" data-testid="best-performing"><Icon name="trend" size={12} /> Best performing</span> : <span className="h-6 text-[12px] text-[var(--fr-text-3)]">{voice.tagline}</span>}
                     </div>
                     <button type="button" aria-label={`Switch to ${nextVoice.name}`} onClick={() => pickVoice(nextVoice)} className="mt-8 opacity-30 hover:opacity-60 transition-opacity"><VoiceOrb palette={nextVoice.palette} size={48} /></button>
                  </div>
                  <button type="button" aria-label="Next voice" onClick={() => pickVoice(nextVoice)} data-testid="voice-next" className="mt-[34px] w-11 h-11 rounded-full border border-[var(--fr-border)] bg-white flex items-center justify-center text-[var(--fr-text-2)] hover:border-[var(--fr-border-strong)] hover:text-[var(--fr-text)]"><Icon name="arrow-right" size={16} /></button>
               </div>
               <div className="mt-3 flex flex-col items-center gap-1.5">
                  <Button variant="secondary" size="sm" onClick={() => setPlaying(playing === voice.id ? null : voice.id)} testId="play">
                     <Icon name={playing === voice.id ? 'stop' : 'play'} size={13} /> {playing === voice.id ? 'Stop' : `Play ${voice.name}`}
                  </Button>
                  <span className="text-[12px] text-[var(--fr-text-3)]">{voiceIndex + 1} of {VOICES.length} voices</span>
               </div>
               <div className="mt-4 max-w-[400px] mx-auto">
                  <Input label="Name it introduces itself with" value={draft.assistantName} onChange={(v) => updateDraft({ assistantName: v })} placeholder={voice.name} hint={<span className="block text-center">You can change this name any time. Leads hear “Hi, this is {draft.assistantName || voice.name} from Patel Realty.”</span>} testId="assistant-name" align="center" />
               </div>
            </Screen>
         )}

         {stage === 'numbers' && (
            <Screen stepKey="numbers" kind="area" title="Which area codes should this campaign call from?" lede="Leads see a caller from these areas. Up to three; the first is used most." width="sm">
               <div className="flex flex-wrap justify-center gap-2" data-testid="area-editor">
                  {Array.from(new Set([...areaCodes, '719', '303', '720'])).map((c) => (
                     <button key={c} type="button" onClick={() => set('areaCodes', areaCodes.includes(c) ? (areaCodes.length > 1 ? areaCodes.filter((x) => x !== c) : areaCodes) : areaCodes.length < 3 ? [...areaCodes, c] : areaCodes)} data-testid={`code-${c}`} className={cn('h-11 px-5 rounded-full border text-[14px] font-semibold transition-colors', areaCodes.includes(c) ? 'border-[var(--fr-blue)] bg-[var(--fr-blue)] text-white' : 'border-[var(--fr-border)] hover:border-[var(--fr-border-strong)]')}>({c})</button>
                  ))}
               </div>
               <div className="mt-4 flex items-end gap-2">
                  <Input label="Another area code" value={areaSearch} onChange={(v) => setAreaSearch(v.replace(/\D/g, '').slice(0, 3))} placeholder="3 digits, e.g. 616" className="flex-1" leading={<Icon name="pin" size={16} />} />
                  <Button variant="secondary" disabled={areaSearch.length !== 3 || areaCodes.length >= 3} onClick={() => { set('areaCodes', [...areaCodes, areaSearch]); setAreaSearch('') }}>Add</Button>
               </div>
            </Screen>
         )}
         {stage === 'hours' && (
            <Screen stepKey="hours" kind="budget" title="When should we call?" lede="Calls and texts only go out in these hours, in your local time." width="md">
               <div className="space-y-5" data-testid="hours-editor">{dayChips(callDays, setCallDays)}{timeRow(callFrom, callTo, setCallFrom, setCallTo)}</div>
            </Screen>
         )}
         {stage === 'book' && (
            <Screen stepKey="book" kind="calendar" title="When can we book appointments for you?" lede="When a lead is ready and you can’t take the call, we book a time inside these hours." width="md">
               <div className="space-y-5" data-testid="book-editor">{dayChips(bookDays, setBookDays)}{timeRow(bookFrom, bookTo, setBookFrom, setBookTo)}</div>
            </Screen>
         )}
         {stage === 'launching' && (
            <div className="h-full flex flex-col items-center justify-center px-8" data-testid="launch-progress">
               <Circle kind="launch" />
               <h1 className="mt-6 text-[32px] md:text-[38px] leading-[1.1] font-semibold tracking-[-0.02em] text-center">{launchStep >= LAUNCH.length ? `${draft.assistantName} is calling` : 'Launching your campaign'}</h1>
               <p className="mt-3 text-[16px] text-[var(--fr-text-2)]">{launchStep >= LAUNCH.length ? 'Taking you to your campaigns.' : 'About twenty seconds. Nothing to do.'}</p>
               <ul className="mt-8 space-y-3 w-[320px]">
                  {LAUNCH.map((l, i) => {
                     const st = i < launchStep ? 'done' : i === launchStep ? 'now' : 'next'
                     return (
                        <li key={l} className="flex items-center gap-3 text-[15px]">
                           {st === 'done' ? <span className="w-6 h-6 rounded-full bg-[var(--fr-good)] text-white flex items-center justify-center"><Icon name="check" size={13} /></span> : <span className={cn('w-6 h-6 rounded-full border-2', st === 'now' ? 'border-[var(--fr-blue)] border-t-transparent animate-spin' : 'border-[var(--fr-border-strong)]')} />}
                           <span className={st === 'next' ? 'text-[var(--fr-text-3)]' : ''}>{l}</span>
                        </li>
                     )
                  })}
               </ul>
            </div>
         )}
         <RailBridge
            stage={stage}
            rows={[
               { id: 'who', label: 'Who we call', icon: 'users', value: ready ? draft.name : 'Not picked yet', sub: ready ? `${draft.leads.toLocaleString()} leads` : undefined, pending: !ready, onEdit: ready ? () => setStage('who') : undefined },
               ...(seats > 1 ? [{ id: 'routing', label: 'Who takes the leads', icon: 'phone', value: routingLabel, sub: `${draft.agents.length} of ${people.length} agents`, pending: !ready && stage !== 'routing', onEdit: ready ? () => setStage('routing') : undefined }] : []),
               { id: 'voice', label: 'Voice', icon: 'voice', value: `${draft.assistantName}${draft.assistantName !== voice.name ? ` (${voice.name})` : ''}`, pending: !ready && stage !== 'voice', onEdit: ready ? () => setStage('voice') : undefined },
               { id: 'numbers', label: 'Calling from', icon: 'pin', value: `${areaCodes.length} local ${areaCodes.length === 1 ? 'number' : 'numbers'}`, sub: areaCodes.map((c) => `(${c})`).join(' '), onEdit: () => openEditor('numbers') },
               { id: 'hours', label: 'Calling hours', icon: 'clock', value: hoursText(callDays, callFrom, callTo), onEdit: () => openEditor('hours') },
               { id: 'book', label: 'Booking hours', icon: 'calendar', value: hoursText(bookDays, bookFrom, bookTo), onEdit: () => openEditor('book') },
            ]}
         />
      </>
   )
}

/* The rail rows are computed inside the inner component (they depend on
 * its state) but rendered by the frame's aside. A tiny bridge passes them up. */
let railRows: IRailRow[] = []
let railListeners: Array<() => void> = []
const RailBridge = ({ rows, stage }: { rows: IRailRow[]; stage: TStageId }) => {
   useEffect(() => {
      railRows = rows
      railListeners.forEach((l) => l())
   })
   void stage
   return null
}
const RailView = ({ stage }: { stage: TStageId }) => {
   const [, force] = useState(0)
   useEffect(() => {
      const l = () => force((n) => n + 1)
      railListeners.push(l)
      return () => {
         railListeners = railListeners.filter((x) => x !== l)
      }
   }, [])
   return <CampaignRail rows={railRows} stage={stage} />
}

/** The composer inside the frame, so its footer lands in the fixed action bar. */
export const Composer = () => {
   const [stage, setStage] = useState<TStageId>('who')
   const [returnTo, setReturnTo] = useState<TStageId>('who')
   return (
      <Frame title="New campaign" rail={<RailView stage={stage} />}>
         <AnimatePresence mode="wait" initial={false}>
            <motion.div key={stage} className="h-full" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
               <ComposerInner stage={stage} setStage={setStage} returnTo={returnTo} setReturnTo={setReturnTo} />
            </motion.div>
         </AnimatePresence>
      </Frame>
   )
}
